// A bare `<v-chart>` GROUPED bar chart — no card, like BaseBarChart: the page owns the card, the
// title and any toolbar. Several bar series side by side over one category axis (one group of bars
// per row), vertical or horizontal, with an optional legend and — for many rows — a scrollable
// window (dataZoom). The sibling of BaseBarChart (one series) in this package, born in the host
// layout at AM_ABSORPTION_PLAN step D3: it replaces standalone asset-management's
// `PlannedInspectedBarChart.vue` (×3 — "Planned / Inspected by Drawing / Category / Type"), whose
// query, colours and click-to-filter moved to the app (`charts/configs/usePlannedInspectedChart.js`).
//
//   props:  rows          (Array)    [{ id?, label, [series key]: number, … }] in display order
//           series        (Array)    [{ key, name?, color? }] — one bar series per entry, in order;
//                                    `key` reads each row's value, `name` (already translated) is the
//                                    legend / tooltip label, `color` a theme colour (null =
//                                    chart.series[index])
//           orientation   (String)   'vertical' (categories on x) | 'horizontal' (on y)
//           valueAxisName (String)   value-axis title — already translated
//           formatValue   (Function) (value) => string; tooltip + value-axis labels
//           formatTooltip (Function) (row, points) => html; the whole tooltip (null = the default)
//           legend        (Boolean)  a legend above the plot (one entry per series)
//           borderRadius  (Number)   rounded bar end, in px (0 = square)
//           barMaxWidth   (Number)   the bars' max width in px (null = ECharts')
//           valueMinInterval (Number) the value axis' minInterval (1 = whole-number ticks)
//           labelRotate   (Number)   category-axis label rotation
//           labelWidth    (Number)   category labels truncated past this width in px (null = none)
//           labelInterval (Number|String) category label interval ('auto' = thinned; null = all)
//           visibleCount  (Number)   more rows than this → a window of the LAST `visibleCount` rows
//                                    (the top of a horizontal chart) with a scroll slider + wheel
//                                    scrolling; null = never
//           selectedId    (String|Number) the selected row's id: every other row's bars are drawn at
//                                    the theme's `dimOpacity` (click-to-filter feedback)
//           onSelect      (Function) (row) => void on a bar click (any series) — a function prop
//           height        (Number)   chart height in px
//           exportFilename / exportTitle (String) the opt-in PNG export (BaseBarChart's)
//   setup:  downloadPng()  saves the chart as a PNG (the export's own legend, one entry per series
//                          colour) — the page owns the card and calls this through a template ref
//
// No emits, no strings and no `messages/` (the series names arrive translated), no
// `chartFontColor` prop: colours come from `useChartTheme()` — the series' fallback palette, every
// label, the legend text, the dimmed opacity and the slider. The shared axis skeleton is
// `axisChartOption` (../../helpers/axisChartOption.js), used with its defaults plus opt-in
// arguments only.
//
// Full contract: ../../../README.md → BaseGroupedBarChart. The logic lives in ./setup.js.
//
// The echarts module registration is wiring for the `VChart` registration below — both are what
// the template's `<v-chart>` needs — so it lives here and ./setup.js stays pure logic.
// `LegendComponent` and `DataZoomComponent` (inside + slider) are this chart's own needs.
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { TooltipComponent, GridComponent, LegendComponent, DataZoomComponent } from "echarts/components";
import VChart from "vue-echarts";
import setup from './setup.js';

use([CanvasRenderer, BarChart, TooltipComponent, GridComponent, LegendComponent, DataZoomComponent]);

// A series key may not shadow a row's own fields
const RESERVED_KEYS = ['id', 'label'];

export default {
  name: 'base-grouped-bar-chart',
  props: {
    // [{ id?, label, [series key]: number }] in display order. Reactive BY REFERENCE: the adapter
    // hands over a new array on every change, so there is no deep watch here.
    rows: {
      type: Array,
      required: true
    },
    // [{ key, name?, color? }] — one bar series each, in this order (the legend's order too).
    // Reactive by reference, like `rows`.
    series: {
      type: Array,
      required: true,
      validator: (specs) => specs.every((spec) =>
        spec && typeof spec.key === 'string' && spec.key !== '' && !RESERVED_KEYS.includes(spec.key))
    },
    // 'vertical': categories on x, values on y. 'horizontal': the other way round
    orientation: {
      type: String,
      default: 'vertical',
      validator: (v) => ['vertical', 'horizontal'].includes(v)
    },
    // Value-axis title — already translated by the page
    valueAxisName: {
      type: String,
      default: ''
    },
    // (value) => string, for the tooltip and the value-axis labels
    formatValue: {
      type: Function,
      default: (value) => String(value)
    },
    // (row, points) => html for the whole tooltip; null = '<label>' + one line per series
    formatTooltip: {
      type: Function,
      default: null
    },
    // A legend above the plot, one entry per series (click one to hide/show it — ECharts' own)
    legend: {
      type: Boolean,
      default: true
    },
    // Rounded bar end in px — `[r,r,0,0]` vertical, `[0,r,r,0]` horizontal
    borderRadius: {
      type: Number,
      default: 0
    },
    // The bars' maximum width in px; null = ECharts' width
    barMaxWidth: {
      type: Number,
      default: null
    },
    // The value axis' minInterval (1 = whole-number ticks for counts); null = ECharts'
    valueMinInterval: {
      type: Number,
      default: null
    },
    labelRotate: {
      type: Number,
      default: 0
    },
    // The category labels' max width in px — truncated past it; null = no truncation
    labelWidth: {
      type: Number,
      default: null
    },
    // The category labels' interval ('auto' = ECharts thins them); null = every label
    labelInterval: {
      type: [Number, String],
      default: null
    },
    // More rows than this → show a window of the LAST `visibleCount` rows (the top of a horizontal
    // chart: sort ascending to open on the biggest) and let the user scroll the rest (a slider and
    // the mouse wheel); null = every row, no slider
    visibleCount: {
      type: Number,
      default: null
    },
    // The selected row's `id` (a click-to-filter selection the page owns): every OTHER row's bars
    // are drawn at the theme's `dimOpacity`. null = no selection, nothing dimmed.
    selectedId: {
      type: [String, Number],
      default: null
    },
    // (row) => void on a bar click (whichever series' bar); null disables clicking
    onSelect: {
      type: Function,
      default: null
    },
    height: {
      type: Number,
      default: 400
    },
    // The opt-in PNG export, as BaseBarChart's. No button here — the page owns the card and its
    // title row, and calls `downloadPng()` through a template ref.
    // The PNG's file name; '' = export off (`downloadPng()` is a no-op)
    exportFilename: {
      type: String,
      default: ''
    },
    // The PNG's title (the page's card title); '' = no title band
    exportTitle: {
      type: String,
      default: ''
    }
  },
  // `<v-chart>` must be registered explicitly by a classic component (no `<script setup>`), or Vue
  // logs "Failed to resolve component: v-chart" and renders nothing.
  components: { VChart },
  setup,
};
